/**
 * Per-tour floorplan variants — uploaded from the dashboard's Developer
 * Mode panel. The FE pushes the file directly to Firebase Storage via
 * the Storage SDK (any size, resumable), then registers the upload with
 * the tour-floorplans-api GCF which writes the per-tour variants index.
 *
 * Storage shape (server-side):
 *   - Index:   gs://{bucket}/tours/{tourId}/floorplan-variants.json
 *   - Sources: gs://{bucket}/tours/{tourId}/floorplan-variants/{variantId}.{ext}
 */

import {
  getStorage,
  ref as storageRef,
  uploadBytesResumable,
} from 'firebase/storage';

const STORAGE_BUCKET =
  process.env.REACT_APP_STORAGE_BUCKET || 'id3d-prod.appspot.com';

export interface FloorplanVariant {
  id: string;
  url: string;
  label: string;
  filename?: string | null;
  contentType?: string | null;
  sizeBytes?: number | null;
  uploadedAt?: string;
}

export interface FloorplanVariantsIndex {
  variants: FloorplanVariant[];
  defaultId: string | null;
  updatedAt?: string;
}

function baseUrl(): string {
  const url = process.env.REACT_APP_FLOORPLANS_API_BASE_URL;
  if (!url) {
    throw new Error('REACT_APP_FLOORPLANS_API_BASE_URL is not configured');
  }
  return url.replace(/\/$/, '');
}

function stripPrefix(tourId: string): string {
  return tourId.startsWith('tours/') ? tourId.slice(6) : tourId;
}

function extensionFor(file: File): string {
  const mime = file.type;
  if (/png/.test(mime)) return 'png';
  if (/jpeg|jpg/.test(mime)) return 'jpg';
  if (/svg/.test(mime)) return 'svg';
  if (/webp/.test(mime)) return 'webp';
  const m = /\.([a-z0-9]+)$/i.exec(file.name);
  return m ? m[1].toLowerCase() : 'png';
}

export const MAX_FLOORPLAN_UPLOAD_BYTES = 50 * 1024 * 1024;

export interface UploadProgress {
  bytesTransferred: number;
  totalBytes: number;
  fraction: number;
}

export async function getTourFloorplans(
  tourId: string,
): Promise<FloorplanVariantsIndex> {
  // Public reads — bypass the function and hit the GCS object directly so
  // we don't pay cold-start latency. The function writes with no-cache,
  // so a cache-busting query string is enough to defeat any CDN copy.
  const id = stripPrefix(tourId);
  const url = `https://storage.googleapis.com/${STORAGE_BUCKET}/tours/${id}/floorplan-variants.json?_=${Date.now()}`;
  const res = await fetch(url);
  if (res.status === 404) return { variants: [], defaultId: null };
  if (!res.ok) {
    throw new Error(
      `floorplan variants GET returned ${res.status} ${res.statusText}`,
    );
  }
  const data = (await res.json()) as Partial<FloorplanVariantsIndex>;
  return {
    variants: Array.isArray(data.variants) ? data.variants : [],
    defaultId: typeof data.defaultId === 'string' ? data.defaultId : null,
    updatedAt: data.updatedAt,
  };
}

export async function uploadTourFloorplan(
  tourId: string,
  file: File,
  options: {
    label?: string;
    onProgress?: (p: UploadProgress) => void;
  } = {},
): Promise<{ variant: FloorplanVariant; defaultId: string | null }> {
  if (!file.type.startsWith('image/')) {
    throw new Error('File must be an image');
  }
  if (file.size > MAX_FLOORPLAN_UPLOAD_BYTES) {
    throw new Error(
      `Floorplan must be ${
        MAX_FLOORPLAN_UPLOAD_BYTES / 1024 / 1024
      } MB or smaller`,
    );
  }

  const id = stripPrefix(tourId);
  const ext = extensionFor(file);
  const variantId = `fpv_${Date.now()}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;
  const objectPath = `tours/${id}/floorplan-variants/${variantId}.${ext}`;

  const storage = getStorage();
  const objectReference = storageRef(storage, objectPath);
  const task = uploadBytesResumable(objectReference, file, {
    contentType: file.type,
  });

  await new Promise<void>((resolve, reject) => {
    task.on(
      'state_changed',
      (snap) => {
        options.onProgress?.({
          bytesTransferred: snap.bytesTransferred,
          totalBytes: snap.totalBytes,
          fraction:
            snap.totalBytes > 0 ? snap.bytesTransferred / snap.totalBytes : 0,
        });
      },
      (err) => reject(err),
      () => resolve(),
    );
  });

  const res = await fetch(`${baseUrl()}/tourFloorplansSave`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      tourId: id,
      variantId,
      objectPath,
      label: options.label,
      filename: file.name,
      contentType: file.type,
      sizeBytes: file.size,
    }),
  });
  if (!res.ok) {
    let detail = '';
    try {
      detail = (await res.json())?.error || '';
    } catch {
      /* ignore */
    }
    throw new Error(detail || `floorplan register returned ${res.status}`);
  }
  const body = (await res.json()) as {
    variant: FloorplanVariant;
    defaultId: string | null;
  };
  return { variant: body.variant, defaultId: body.defaultId };
}

export async function setDefaultFloorplan(
  tourId: string,
  variantId: string | null,
): Promise<void> {
  const res = await fetch(`${baseUrl()}/tourFloorplansSetDefault`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tourId: stripPrefix(tourId), variantId }),
  });
  if (!res.ok) {
    let detail = '';
    try {
      detail = (await res.json())?.error || '';
    } catch {
      /* ignore */
    }
    throw new Error(detail || `setDefaultFloorplan returned ${res.status}`);
  }
}

export async function renameTourFloorplan(
  tourId: string,
  variantId: string,
  label: string,
): Promise<void> {
  const res = await fetch(`${baseUrl()}/tourFloorplansUpdate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tourId: stripPrefix(tourId), variantId, label }),
  });
  if (!res.ok) {
    let detail = '';
    try {
      detail = (await res.json())?.error || '';
    } catch {
      /* ignore */
    }
    throw new Error(detail || `renameTourFloorplan returned ${res.status}`);
  }
}

export async function deleteTourFloorplan(
  tourId: string,
  variantId: string,
): Promise<void> {
  const res = await fetch(`${baseUrl()}/tourFloorplansDelete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tourId: stripPrefix(tourId), variantId }),
  });
  if (!res.ok) {
    let detail = '';
    try {
      detail = (await res.json())?.error || '';
    } catch {
      /* ignore */
    }
    throw new Error(detail || `deleteTourFloorplan returned ${res.status}`);
  }
}
